import React, { useContext, useEffect, useState } from "react";
import styled from "styled-components";
import {
    Button, ErrorLabel, Toast, LottieLoadingIndicator,
    StandardModal,
    StandardModalActionGroup,
    UserPermissionModalContainer,
    PermissionModalSection,
    PermissionModalRowLabel,
    PermissionField,
    PermissionInput
} from "src/components";
import { portalColors } from "src/components/BusinessPortalComponents";
import { PermissionModalActionRow } from "src/components/PermissionModalStyles";
import MoreAboutPermissions from "./MoreAboutPermissions";
import RolesForm from "./RolesForm";
import { ROLE_STYLES, STANDARD, MULTIPLE_PERMISSIONS, ACCOUNT_OWNER } from "src/constants/roles";
import {
    FULL_ACCESS_BUSINESS_CHAT,
    MESSAGE_CLIENTS,
    MESSAGE_EMPLOYEES,
    CREATE_GROUP_CHAT,
    MY_QUOTE,
    BUSINESS_DIRECTORY,
    DIGITAL_MEMBER_CARDS,
    SALES_AND_PROMOTIONS,
    BODY_AND_MIND,
    TEAM_GOALS,
    CHECKLISTS,
    WEB_DASHBOARD_ACCESS
} from "src/constants/permissions";
import {
    getDisplayPermissionSelection,
    getNextPermissionSelection,
    getSavePermissionSelection
} from "./permissionPresentation";
import {
    useSavePermissions
} from "../hooks";
import pluralize from "pluralize";
import { customNames } from "src/utils/helpers";
import { UserKeys } from "src/constants/userDetails";
import { DefaultMyQuotesFeatureName } from "src/scenes/MyQuotes/Myquotes";
import { DefaultDirectoryFeatureName } from "src/scenes/Directory/Directory";
import { getMembershipCardHeader } from "src/scenes/DigitalMemberCard";
import { getSalesAndPromotionHeading } from "src/scenes/ClientLicense/components/SalesAndPromotionHeader";
import { DefaultBodyAndMindFeatureName } from "src/scenes/BodyAndMind/BodyAndMindList";
import { FEATURE_FLAGS, hasFeature } from "src/constants/FeatureFlag";
import BulkPermissionAssignment from "./BulkPermissionAssignment";
import DefaultPermissionAssignment from "src/components/DefaultPermissionAssignment";
import { UserContext } from "src/scenes/App/UserContext";
import { USER_LISTS, hasPermissions } from "src/constants/permissions";
import {
    getDefaultPermissions, saveDefaultPermissions, getPermissionsForUser, PERMISSION_USER_GROUP
} from "../hooks/permissions/defaultPermissions";
import {
    USER_INFORMATION_MODAL_WIDTH,
    USER_PERMISSIONS_MODAL_WIDTH
} from "./permissionModalConstants";

const FormButtons = styled(Button)`
    border-radius: 7px !important;
    box-shadow: none;
    width: 100%;
    max-width: 8.75rem;
`;

const UserTypeSelect = styled.select`
    width: 200px;
    height: 41px;
    background: ${portalColors.surface};
    border: 1px solid ${portalColors.border};
    border-radius: 8px;
    color: ${portalColors.text};
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.875rem;
    font-weight: 700;
    padding: 0 0.75rem;
`;

const BulkFooterWarning = styled.div`
    color: #8e4eb4;
    flex: 1;
    font-size: 0.875rem;
    font-weight: 500;
    line-height: 1.25;
    margin-right: 1.25rem;
    text-align: left;
`;

const PermissionsHeaderActions = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 0.75rem;
    flex-wrap: wrap;
`;

const UserInfoGrid = styled.div`
    display: grid;
    grid-template-columns: ${p => p.$isBulk ? "1fr" : "repeat(3, minmax(0, 1fr))"};
    gap: 0.85rem;

    @media (max-width: 760px) {
        grid-template-columns: 1fr;
    }
`;

const PermissionFooterActionGroup = styled(StandardModalActionGroup)`
    @media (max-width: 640px) {
        flex-direction: row;
        justify-content: flex-end;

        > *,
        button {
            width: auto;
            max-width: 8.75rem;
            flex: 0 0 auto;
        }
    }

    @media (max-width: 360px) {
        > *,
        button {
            width: 100%;
            max-width: 100%;
        }
    }
`;


function UserRoleAndPermissionsForm(props) {
    const { showDialog, onFormSubmissionSuccessful, handleClose, token, hasLeadershipRole } = props;
    const [ isDataFetchFinished, setIsDataFetchFinished ] = useState(false);
    const [ permissions, setPermissions ] = useState(null);
    const roles = props.roles || [];
    const bulkUsers = props.users || [];
    const isBulk = Boolean(props.isBulk);
    const [ currentSelectedRole, setCurrentSelectedRole ] = useState(props.userRole);
    const [ currentSelectedPermissions, setCurrentSelectedPermissions ] = useState(getDisplayPermissionSelection(props.userPermissions));
    const [ selectedBulkUserUuids, setSelectedBulkUserUuids ] = useState([]);
    const [ bulkSearchKey, setBulkSearchKey ] = useState("");
    const [ disableInput, setDisableInput ] = useState(false);
    const [ error, setError ] = useState(null);
    const [defaultEnabled, setDefaultEnabled] = useState(false);
    const [hadDefault, setHadDefault] = useState(false);
    const [loadedDefaultGroup, setLoadedDefaultGroup] = useState(null);
    const userContext = useContext(UserContext);
    const defaultGroup = props.permissionSource === "sales" ? PERMISSION_USER_GROUP.SALES_CLIENTS
        : props.isClient ? PERMISSION_USER_GROUP.USER_LIST_CLIENTS : PERMISSION_USER_GROUP.EMPLOYEES;
    const canManageDefaults = isBulk && hasPermissions(
        [props.permissionSource === "sales" ? SALES_AND_PROMOTIONS : USER_LISTS], userContext.permissions
    );
    const defaultsReady = !canManageDefaults || loadedDefaultGroup === defaultGroup;

    useEffect(() => {
        const controller = new AbortController();
        setLoadedDefaultGroup(null);
        setDefaultEnabled(false);
        setHadDefault(false);
        setError(null);
        if (canManageDefaults) {
            getDefaultPermissions(defaultGroup, token, controller.signal).then(permissionIds => {
                if (controller.signal.aborted) {
                    return;
                }
                setDefaultEnabled(permissionIds !== null);
                setHadDefault(permissionIds !== null);
                setCurrentSelectedPermissions(getDisplayPermissionSelection(permissionIds || []));
                setLoadedDefaultGroup(defaultGroup);
            }).catch(error => {
                if (!controller.signal.aborted) {
                    setError(error.getApiErrorDetails().message);
                }
            });
        }
        return () => controller.abort();
    }, [canManageDefaults, defaultGroup, token]);

    const { isSavingPermissions, savePermissions, savePermissionsBulk } = useSavePermissions();

    const headers = { headers: { Authorization: "Bearer " + token } };
    const userRoleStylesForInput = ROLE_STYLES;
    
    const customPermissionLabels = {
        [MY_QUOTE]: props.user?.[UserKeys.CUSTOM_NAME_MY_QUOTES] || DefaultMyQuotesFeatureName,
        [BUSINESS_DIRECTORY]: props.user?.[UserKeys.CUSTOM_NAME_DIRECTORY] || DefaultDirectoryFeatureName,
        [DIGITAL_MEMBER_CARDS]: getMembershipCardHeader(props.user),
        [SALES_AND_PROMOTIONS]: getSalesAndPromotionHeading(props.user),
        [BODY_AND_MIND]: props.user?.[UserKeys.CUSTOM_NAME_BODY_AND_MIND] || DefaultBodyAndMindFeatureName
    };
    const hiddenPermissionIds = [
        WEB_DASHBOARD_ACCESS,
        ...(!hasFeature(props.user, FEATURE_FLAGS.TEAM_GOALS) ? [TEAM_GOALS] : []),
        ...(!hasFeature(props.user, FEATURE_FLAGS.CHECKLIST) ? [CHECKLISTS] : [])
    ];
    const hiddenPermissionIdSet = new Set(hiddenPermissionIds);
    const normalizePermissionIdsForRoleMatch = (permissionIds) => (
        permissionIds
            .map(Number)
            .filter(permissionId => !hiddenPermissionIdSet.has(permissionId))
            .sort((a, b) => a - b)
    );


    useEffect(() => {
        // This will check whether the 'checked' permissions matches those of any role's default permissions;
        // if it does not match, the role should automatically become "Multiple Permissions"
        let roleToSet = MULTIPLE_PERMISSIONS;
        const selected = normalizePermissionIdsForRoleMatch(getSavePermissionSelection(currentSelectedPermissions));

        for (let i = 0; i < roles.length; i++) {
            const arr2 = normalizePermissionIdsForRoleMatch(roles[i].permissionIds);
            if (selected.length === arr2.length) {
                const status = selected.every((v, i) => v === arr2[i]);
                if (status) {
                    roleToSet = roles[i].id;
                    break;
                }
            }
        }

        setCurrentSelectedRole(roleToSet);
    }, [currentSelectedPermissions, roles]);

    const getPermissions = async (isClient, signal) => {
        try {
            const permissions = await getPermissionsForUser(isClient, token, signal);
            if (signal.aborted) {
                return;
            }
            setPermissions(permissions);
            renderForm();
            toggleDisableOnButtons();
        } catch (error) {
            if (!signal.aborted) {
                Toast.error(error.getApiErrorDetails().message);
            }
        }
    };

    const renderForm = () => {
        setIsDataFetchFinished(true);
    };

    const clickRole = (roleId) => {
        const item = roles.find(role => role.id === roleId);
        setCurrentSelectedRole(roleId);
        setCurrentSelectedPermissions(getDisplayPermissionSelection(item.permissionIds));
    };

    const handleFormSubmit = async (event) => {
        event.preventDefault();
        if (disableInput || !isDataFetchFinished || !defaultsReady) {
            return;
        }
        if (isBulk && selectedBulkUserUuids.length === 0 && !(canManageDefaults && (defaultEnabled || hadDefault))) {
            return Toast.error("Select at least one user to update.");
        }

        setDisableInput(true);
        toggleDisableOnButtons();
        try {
            const permissionIdsToSave = getSavePermissionSelection(currentSelectedPermissions)
                .filter(permissionId => !hiddenPermissionIdSet.has(permissionId));

            if (canManageDefaults) {
                await saveDefaultPermissions(defaultGroup, defaultEnabled, permissionIdsToSave, selectedBulkUserUuids, token);
                Toast.success("User roles, permissions and defaults have been updated.");
            } else if (isBulk) {
                await savePermissionsBulk(selectedBulkUserUuids, permissionIdsToSave, headers);
            } else if (props.userRole !== ACCOUNT_OWNER) {
                await savePermissions(props.userUuid, permissionIdsToSave, headers);
            }
            onFormSubmissionSuccessful();
        } catch (error) {
            setError(error.getApiErrorDetails().message);
        } finally {
            setDisableInput(false);
            toggleDisableOnButtons();
        }
    };

    const toggleDisableOnButtons = () => {
        const buttons = document.querySelectorAll(".form-btns");
        Array.from(buttons).forEach(button => {
            button.classList.toggle("disabled");
        });
    };

    /**
     * disable checkbox for remaining permission when FULL_ACCESS_BUSINESS_CHAT is selected */
    const disableChatPermission = (permissionId) => {
        if (disableInput) {
            return true;
        }
    
        const permissionsThatCanBeDisabled = [MESSAGE_CLIENTS, MESSAGE_EMPLOYEES, CREATE_GROUP_CHAT];
        if (permissionsThatCanBeDisabled.includes(permissionId)) {
            if (currentSelectedPermissions.includes(FULL_ACCESS_BUSINESS_CHAT)) {
                return true;
            }
        }

        return false;
    };

    const setPermissionIsChecked = (permissionId, isChecked) => {
        setCurrentSelectedPermissions(getNextPermissionSelection(currentSelectedPermissions, permissionId, isChecked));
    };

    const getRoleInputStyles = (key) => userRoleStylesForInput[currentSelectedRole]?.[key] || userRoleStylesForInput[props.userRole]?.[key] || userRoleStylesForInput[0][key];
    const renderRoleInputText = (name) => currentSelectedRole === STANDARD ? `${name} (${ props.isClient ? `${pluralize.singular(customNames.clients)}` : `${pluralize.singular(customNames.employees)}` })` : name;
    const isUserRole = (roleId) => currentSelectedRole === roleId;
    const userTypeLabel = props.isClient ? pluralize(props.customNames?.clients || customNames.clients) : pluralize(props.customNames?.employees || customNames.employees);
    const userTypeLabelSingular = props.isClient ? pluralize.singular(props.customNames?.clients || customNames.clients) : pluralize.singular(props.customNames?.employees || customNames.employees);
    const selectableBulkUsers = bulkUsers.filter(user => user.roleAndPermissions?.roleId !== ACCOUNT_OWNER);
    const allBulkUsersSelected = selectableBulkUsers.length > 0 && selectableBulkUsers.every(user => selectedBulkUserUuids.includes(user.uuid));
    const filteredBulkUsers = bulkUsers.filter(user => {
        if (!bulkSearchKey) {
            return true;
        }

        const search = bulkSearchKey.toLowerCase();
        const valuesToSearch = [
            user.firstName,
            user.lastName,
            user.email,
            user.department,
            user.employmentPosition,
            `${user.firstName || ""} ${user.lastName || ""}`,
        ];

        return valuesToSearch.some(value => (value || "").toLowerCase().includes(search));
    });

    const toggleBulkUser = (userUuid) => {
        setSelectedBulkUserUuids(prev => (
            prev.includes(userUuid)
                ? prev.filter(uuid => uuid !== userUuid)
                : [...prev, userUuid]
        ));
    };

    const selectAllBulkUsers = (isChecked) => {
        setSelectedBulkUserUuids(isChecked ? selectableBulkUsers.map(user => user.uuid) : []);
    };

    const handleBulkUserTypeChange = (event) => {
        const nextIsClient = event.target.value === "clients";
        setSelectedBulkUserUuids([]);
        setCurrentSelectedPermissions([]);
        setBulkSearchKey("");
        props.onBulkUserTypeChange?.(nextIsClient);
    };

    useEffect(() => {
        // this will abort ongoing async calls when component unmounts
        const controller = new AbortController();
        const { signal } = controller;
        setIsDataFetchFinished(false);
        getPermissions(props.isClient, signal);

        return () => controller.abort();
    }, [props.isClient]);

    return (
        <React.Fragment>
            <StandardModal
                open={showDialog}
                onClose={handleClose}
                widthMode={isBulk ? USER_PERMISSIONS_MODAL_WIDTH : USER_INFORMATION_MODAL_WIDTH}
                title={isBulk ? "User Permissions & Roles" : "User Information"}
                description="User permissions are rules that control what actions a user can perform on your business app. They help protect your privacy and security by giving you control over what information or features a user can access."
                headerAside={(
                    <PermissionsHeaderActions>
                        { isBulk && !props.lockBulkUserType && (
                            <UserTypeSelect
                                value={props.isClient ? "clients" : "employees"}
                                onChange={handleBulkUserTypeChange}
                                disabled={disableInput || !defaultsReady}
                            >
                                <option value="employees">{pluralize(props.customNames?.employees || customNames.employees)}</option>
                                <option value="clients">{pluralize(props.customNames?.clients || customNames.clients)}</option>
                            </UserTypeSelect>
                        )}
                        <MoreAboutPermissions
                            permissions={permissions}
                            customPermissionLabels={customPermissionLabels}
                            hiddenPermissionIds={hiddenPermissionIds}
                        />
                    </PermissionsHeaderActions>
                )}
                footer={(
                    <PermissionModalActionRow>
                        {isBulk && (
                            <BulkFooterWarning>
                                *Applying the selected role and permissions will overwrite and replace any existing access,
                                permissions, or role settings currently assigned to the selected users. Please review your selection carefully before continuing.
                            </BulkFooterWarning>
                        )}
                        <PermissionFooterActionGroup $pushRight>
                            <FormButtons color="#000"
                                border="1px solid #000"
                                onClick={handleClose}
                                className="form-btns disabled"
                            >
                                Cancel
                            </FormButtons>
                            <FormButtons color="#FFF"
                                backgroundColor="#171A22"
                                border="1px solid #171A22"
                                type="submit"
                                disabled={disableInput || !isDataFetchFinished || !defaultsReady}
                                onClick={handleFormSubmit}
                                className="form-btns disabled"
                            >
                                { disableInput || isSavingPermissions ? "Loading..." : isBulk ? "Confirm & Save" : "Save" }
                            </FormButtons>
                        </PermissionFooterActionGroup>
                    </PermissionModalActionRow>
                )}
            >
                <UserPermissionModalContainer>
                    {!isBulk && (
                        <PermissionModalSection>
                            <PermissionModalRowLabel>User Information</PermissionModalRowLabel>
                            <UserInfoGrid $isBulk={isBulk}>
                                <PermissionField>
                                    <label>Name</label>
                                    <PermissionInput value={props.userName} onChange={() => ""} />
                                </PermissionField>
                                <PermissionField>
                                    <label>Email</label>
                                    <PermissionInput style={{ paddingRight: 20 }} value={props.userEmail} onChange={() => ""} />
                                </PermissionField>
                                <PermissionField>
                                    <label>User Type</label>
                                    <PermissionInput
                                        value={renderRoleInputText(getRoleInputStyles("name"))}
                                        onChange={() => ""}
                                        style={{
                                            color: getRoleInputStyles("color"),
                                            background: getRoleInputStyles("background"),
                                            border: `1px solid ${getRoleInputStyles("color")}`,
                                            fontWeight: 500
                                        }}
                                    />
                                </PermissionField>
                            </UserInfoGrid>
                        </PermissionModalSection>
                    )}

                    { !isDataFetchFinished ? <LottieLoadingIndicator /> : 
                        <RolesForm 
                            user={props.user}
                            isClient={props.isClient}
                            roles={roles} 
                            userRole={props.userRole}
                            isUserRole={isUserRole} 
                            clickRole={clickRole} 
                            disableInput={disableInput || !defaultsReady}
                            currentSelectedRole={currentSelectedRole}
                            permissions={permissions}
                            currentSelectedPermissions={currentSelectedPermissions}
                            setPermissionIsChecked={setPermissionIsChecked}
                            disableChatPermission={disableChatPermission}     
                            hasLeadershipRole={hasLeadershipRole}
                            customPermissionLabels={customPermissionLabels}
                            hiddenPermissionIds={hiddenPermissionIds}
                        />
                    }

                    {canManageDefaults && isDataFetchFinished && (
                        <DefaultPermissionAssignment
                            userTypeLabel={userTypeLabel}
                            enabled={defaultEnabled}
                            disabled={disableInput || !defaultsReady}
                            onChange={setDefaultEnabled}
                        />
                    )}

                    {isBulk && isDataFetchFinished && (
                        <BulkPermissionAssignment
                            isDataFetchFinished={isDataFetchFinished}
                            selectedBulkUserUuids={selectedBulkUserUuids}
                            userTypeLabel={userTypeLabel}
                            userTypeLabelSingular={userTypeLabelSingular}
                            bulkSearchKey={bulkSearchKey}
                            setBulkSearchKey={setBulkSearchKey}
                            disableInput={disableInput}
                            allBulkUsersSelected={allBulkUsersSelected}
                            selectAllBulkUsers={selectAllBulkUsers}
                            selectableBulkUsers={selectableBulkUsers}
                            filteredBulkUsers={filteredBulkUsers}
                            toggleBulkUser={toggleBulkUser} />
                    )}

                    { error !== null && <ErrorLabel>{ error }</ErrorLabel> }
                </UserPermissionModalContainer>
            </StandardModal>
        </React.Fragment>
    );
}

export default UserRoleAndPermissionsForm;
